import { JobSettingsSchema, PRESETS, type ColumnMapping, type JobSettings } from '@profiler/shared'

export const DEFAULT_PRESET_ID = 'founders_and_tech'

/** ISO-2 codes offered in the country picker. Free text is still accepted. */
export const COUNTRY_OPTIONS = ['IN', 'US', 'GB', 'AE', 'SG', 'AU', 'CA', 'DE', 'NL', 'FR', 'ZA'] as const

export function presetOptions(): Array<{ id: string; label: string }> {
  return Object.entries(PRESETS).map(([id, preset]) => ({ id, label: preset.label }))
}

export function defaultSettings(): JobSettings {
  const preset = PRESETS[DEFAULT_PRESET_ID]
  // PRESETS is a shared constant and JobSettingsSchema defaults presetId to this
  // key, so absence means the two have drifted apart. Fail loudly rather than
  // starting a job with an empty persona list.
  if (!preset) throw new Error(`PRESETS is missing the default preset "${DEFAULT_PRESET_ID}"`)
  return JobSettingsSchema.parse({ presetId: DEFAULT_PRESET_ID, personas: preset.personas })
}

export interface MappingIssue {
  severity: 'error' | 'warning'
  message: string
}

/**
 * The mapping decides what every downstream rung can even attempt, so its
 * problems are surfaced before upload rather than as an empty result later.
 */
export function checkMapping(mapping: ColumnMapping): MappingIssue[] {
  const roles = Object.values(mapping)
  const issues: MappingIssue[] = []

  const companyNames = roles.filter((r) => r === 'company_name').length
  if (companyNames === 0) {
    issues.push({
      severity: 'error',
      message: 'Map one column to Company name. It is the only required role.',
    })
  } else if (companyNames > 1) {
    issues.push({ severity: 'error', message: 'Only one column can be the Company name.' })
  }

  for (const role of ['website', 'linkedin_url', 'country'] as const) {
    if (roles.filter((r) => r === role).length > 1) {
      issues.push({
        severity: 'error',
        message: `Only one column can be mapped to ${role.replace('_', ' ')}.`,
      })
    }
  }

  if (!roles.includes('website') && !roles.includes('linkedin_url')) {
    issues.push({
      severity: 'warning',
      message:
        'Neither a website nor a LinkedIn URL is mapped. Both zero-credit rungs are unavailable, so every company falls through to a name search.',
    })
  }

  return issues
}

export function hasBlockingIssue(issues: MappingIssue[]): boolean {
  return issues.some((i) => i.severity === 'error')
}
